import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Carte, Chargement, DateHeure, Erreur, formaterDateHeure } from '../../composants/communs.tsx';
import {
  DataTable,
  Montant,
  Pagination,
  formaterFcfa,
  exporterTableauPdf,
  type Colonne,
} from '../../composants/ui/index.ts';
import { useApercuPdf } from '../../composants/apercu-pdf.tsx';
import { imprimerVente } from './imprimer-vente.ts';
import type { DetailVente } from './types.ts';
import type { ReponseVentes, Vente } from './types.ts';

/**
 * Facturation — réimpression des tickets déjà émis.
 *
 * Écran DISTINCT de « Ventes » : ce n'est pas l'historique complet (vendeur,
 * canal, lignes…), mais un point d'entrée unique pour retrouver et
 * réimprimer une facture — le geste qu'un comptable ou un gérant répète le
 * plus souvent, sans avoir à ouvrir chaque vente en détail pour ça.
 *
 * Le filtre boutique est LOCAL à cet écran, indépendant du sélecteur global
 * de contexte : on peut consulter la facturation d'une autre boutique que
 * celle qu'on gère au quotidien, sans changer son contexte de travail.
 */
export function Facturation(): ReactNode {
  const { boutiques } = useSession();
  const [boutiqueId, setBoutiqueId] = useState('');
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [page, setPage] = useState(1);
  const limite = 25;

  // Un filtre qui change doit retomber en page 1 : rester en page 3 d'une
  // recherche qui n'a plus que deux résultats afficherait un écran vide.
  useEffect(() => {
    setPage(1);
  }, [boutiqueId, dateDebut, dateFin]);

  const { data, isPending, error } = useQuery({
    queryKey: ['facturation', boutiqueId, dateDebut, dateFin, page],
    queryFn: () => {
      const parametres = new URLSearchParams();
      if (boutiqueId !== '') parametres.set('boutique_id', boutiqueId);
      if (dateDebut !== '') parametres.set('date_debut', dateDebut);
      if (dateFin !== '') parametres.set('date_fin', dateFin);
      parametres.set('page', String(page));
      parametres.set('limite', String(limite));
      return appeler<ReponseVentes>(`/ventes?${parametres.toString()}`);
    },
  });

  return (
    <>
      <Entete
        titre="Facturation"
        sous="Retrouvez une facture par boutique et par période, et réimprimez-la."
      />

      <Carte>
        <div className="champs">
          <div className="champ">
            <label htmlFor="facturation-boutique">Boutique</label>
            <select
              id="facturation-boutique"
              value={boutiqueId}
              onChange={(evenement) => {
                setBoutiqueId(evenement.target.value);
              }}
            >
              <option value="">Toutes</option>
              {boutiques.map((boutique) => (
                <option key={boutique.id} value={boutique.id}>
                  {boutique.code} — {boutique.nom}
                </option>
              ))}
            </select>
          </div>
          <div className="champ">
            <label htmlFor="facturation-debut">Depuis</label>
            <input
              id="facturation-debut"
              type="date"
              value={dateDebut}
              max={dateFin === '' ? undefined : dateFin}
              onChange={(evenement) => {
                setDateDebut(evenement.target.value);
              }}
            />
          </div>
          <div className="champ">
            <label htmlFor="facturation-fin">Jusqu’au</label>
            <input
              id="facturation-fin"
              type="date"
              value={dateFin}
              min={dateDebut === '' ? undefined : dateDebut}
              onChange={(evenement) => {
                setDateFin(evenement.target.value);
              }}
            />
          </div>
        </div>

        <Erreur erreur={error} />

        {isPending && <Chargement />}
        {data !== undefined && (
          <>
            <Liste ventes={data.donnees} />
            <Pagination page={data.page} limite={data.limite} total={data.total} onPage={setPage} />
          </>
        )}
      </Carte>
    </>
  );
}

function Liste({ ventes }: { ventes: Vente[] }): ReactNode {
  const { ouvrir } = useApercuPdf();
  const colonnes: Colonne<Vente>[] = [
    {
      cle: 'numero',
      entete: 'N°',
      rendu: (vente) => <Link to={`/ventes/${vente.id}`}>{vente.numero}</Link>,
      texte: (vente) => vente.numero,
    },
    {
      cle: 'date',
      entete: 'Date',
      rendu: (vente) => <DateHeure valeur={vente.horodatage_serveur} />,
      texte: (vente) => formaterDateHeure(vente.horodatage_serveur),
    },
    {
      cle: 'boutique',
      entete: 'Boutique',
      rendu: (vente) => vente.boutique,
      texte: (vente) => vente.boutique,
    },
    {
      cle: 'total',
      entete: 'Montant',
      numerique: true,
      rendu: (vente) => <Montant valeur={vente.total} />,
      texte: (vente) => formaterFcfa(vente.total),
    },
    {
      cle: 'reimprimer',
      entete: '',
      rendu: (vente) => <ActionReimprimer vente={vente} />,
      // Pas de `texte` : une action n'a rien à imprimer sur elle-même.
    },
  ];

  return (
    <>
      {ventes.length > 0 && (
        <div className="ligne" style={{ justifyContent: 'flex-end', marginBottom: 8 }}>
          <button
            type="button"
            className="bouton--discret"
            onClick={() => {
              ouvrir(() =>
                exporterTableauPdf({
                  titre: 'Facturation',
                  colonnes,
                  lignes: ventes,
                  nomFichier: 'facturation',
                }),
              );
            }}
          >
            Exporter la liste
          </button>
        </div>
      )}
      <DataTable
        lignes={ventes}
        cleDe={(vente) => vente.id}
        vide="Aucune facture pour ces filtres."
        colonnes={colonnes}
      />
    </>
  );
}

/**
 * Réimprime SANS passer par l'écran de détail : la liste n'a que le
 * résumé (`Vente`), pas les lignes ni les règlements — on va les chercher
 * au clic, pour cette seule facture, plutôt que de tout précharger pour
 * chaque ligne de la liste (un N+1 qu'un gérant qui consulte un mois entier
 * ferait payer au serveur pour rien).
 */
function ActionReimprimer({ vente }: { vente: Vente }): ReactNode {
  const { ouvrir } = useApercuPdf();

  return (
    <button
      type="button"
      className="bouton--discret"
      onClick={() => {
        ouvrir(() => appeler<DetailVente>(`/ventes/${vente.id}`).then(imprimerVente));
      }}
    >
      Réimprimer
    </button>
  );
}
